import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('d3_token'));
  const [loading, setLoading] = useState(true);

  // Safely read response body as JSON (prevents body stream and clone errors)
  const safeJson = async (res) => {
    try {
      const text = await res.text();
      return text ? JSON.parse(text) : {};
    } catch {
      return {};
    }
  };

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const userData = await safeJson(res);
          setUser(userData);
        } else {
          localStorage.removeItem('d3_token');
          setToken(null);
        }
      } catch {
        localStorage.removeItem('d3_token');
        setToken(null);
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = useCallback(async (email, password) => {
    let res;
    try {
      res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
    } catch (networkErr) {
      throw new Error('Unable to connect. Please check your internet connection.');
    }
    const data = await safeJson(res);
    if (!res.ok) throw new Error(data.detail || 'Invalid email or password');
    localStorage.setItem('d3_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const signup = useCallback(async (name, email, password, company, role, inviteToken, pendingPlan) => {
    const body = { name, email, password, company, role };
    if (inviteToken) body.inviteToken = inviteToken;
    if (pendingPlan) body.pendingPlan = pendingPlan;

    let res;
    try {
      res = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (networkErr) {
      throw new Error('Unable to connect. Please check your internet connection.');
    }
    const data = await safeJson(res);
    if (!res.ok) throw new Error(data.detail || 'Signup failed');
    localStorage.setItem('d3_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
      }
    } catch {}
  }, [token]);

  const logout = useCallback(() => {
    localStorage.removeItem('d3_token');
    sessionStorage.removeItem('d3_pending_plan');
    setToken(null);
    setUser(null);
    window.history.pushState({}, '', '/');
  }, []);

  const demoLogin = useCallback(async () => {
    let res;
    try {
      res = await fetch(`${API_URL}/api/auth/demo-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (networkErr) {
      throw new Error('Unable to connect. Please try again.');
    }
    const data = await safeJson(res);
    if (!res.ok) throw new Error(data.detail || 'Demo login failed');
    localStorage.setItem('d3_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    let res;
    try {
      res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch (networkErr) {
      throw new Error('Unable to connect. Please try again.');
    }
    const data = await safeJson(res);
    if (!res.ok) throw new Error(data.detail || 'Request failed');
    return data;
  }, []);

  const resetPassword = useCallback(async (resetToken, newPassword) => {
    let res;
    try {
      res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, newPassword }),
      });
    } catch (networkErr) {
      throw new Error('Unable to connect. Please try again.');
    }
    const data = await safeJson(res);
    if (!res.ok) throw new Error(data.detail || 'Reset failed');
    return data;
  }, []);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    billingRequired: user?.billingRequired === true,
    isDemo: user?.isDemo === true,
    login,
    signup,
    demoLogin,
    logout,
    refreshUser,
    forgotPassword,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
